/**
 * Orders one designer provider's teardown before the next one's load.
 *
 * The screen-design store (`sdStore` in `screen-provider.tsx`) and the designer-live stream are
 * module singletons, shared by every `ScreenDesignContextProvider` instance. A provider's
 * teardown awaits a final `saveScreen(true)` and only then TERMINATEs the store and stops
 * designer-live. When the provider is replaced in one commit — the header's app switcher opening
 * another app, or browser Back across apps — the new provider mounts while that save is still in
 * flight. Left unordered, the old teardown lands after the new app's tree was loaded and wipes
 * it, and the old save can read the store while it already holds the new app's tree.
 *
 * Leaving through the Apps page never hit this only because a person takes seconds to pick the
 * next app. The gate makes the order explicit instead of relying on that pause.
 */
let pendingTeardown: Promise<void> = Promise.resolve();

/**
 * How long the next app waits for a teardown at most. The final save has no request timeout of
 * its own, and a hung save must not leave the next app on its loader forever — before the gate
 * it loaded regardless. Well past a normal save (sub-second), short of anyone giving up.
 */
export const DESIGNER_TEARDOWN_WAIT_MS: number = 15000;

/** Records the teardown now running. A failed or hung teardown still releases the next load. */
export const trackDesignerTeardown = (teardown: Promise<void>): void => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout: Promise<void> = new Promise<void>((resolve: () => void): void => {
    timer = setTimeout((): void => {
      console.warn("[designer] previous designer teardown still running after 15s; loading the next app");
      resolve();
    }, DESIGNER_TEARDOWN_WAIT_MS);
  });
  const settled: Promise<void> = teardown.catch((): void => undefined).finally((): void => clearTimeout(timer));
  pendingTeardown = Promise.race([settled, timeout]);
};

/** Resolves once the previous provider's teardown — save, TERMINATE, leave — has finished. */
export const whenPreviousDesignerTornDown = (): Promise<void> => pendingTeardown;
